import { useState, useEffect } from 'react'
import { Dialog, useMediaQuery, useTheme } from '@mui/material'

import RecordingStep from '../AudioPromptWizard/steps/RecordingStep'
import ReviewStep from '../AudioPromptWizard/steps/ReviewStep'
import VerifyPhrase from '../AudioPromptWizard/steps/VerifyPhraseStep'
import ReviewVerifiedPhraseStep from '../AudioPromptWizard/steps/ReviewVerifiedPhraseStep'

import useRecorder from '@/utils/useRecorder'
import { useAuthUser } from '@/hooks/useUser'
import useOpenAI from '@/hooks/useOpenAI'
import { type CardActivity } from '@/models/card-activity/card-activity.model'

export type RepeatAudioStepKey = 'record-pronunciation' | 'review-recorded-pronunciation' | 'verify-phrase'

export type WizardMode = 'add-pronunciation' | 'verify-term'

type StepsConfig = {
  [key in WizardMode]: {
    initial: RepeatAudioStepKey
    flow: Record<RepeatAudioStepKey, RepeatAudioStepKey[]>
  }
}

const steps: Record<RepeatAudioStepKey, React.ElementType> = {
  'record-pronunciation': RecordingStep,
  'review-recorded-pronunciation': ReviewStep,
  'verify-phrase': VerifyPhrase,
}

const stepsConfig: StepsConfig = {
  'add-pronunciation': {
    initial: 'record-pronunciation',
    flow: {
      'record-pronunciation': ['record-pronunciation', 'review-recorded-pronunciation'],
      'review-recorded-pronunciation': ['record-pronunciation', 'review-recorded-pronunciation'],
      'verify-phrase': [],
    },
  },
  'verify-term': {
    initial: 'verify-phrase',
    flow: {
      'record-pronunciation': [],
      'review-recorded-pronunciation': [],
      'verify-phrase': ['verify-phrase'],
    },
  },
}

interface RepeatAudioWizardProps {
  mode?: WizardMode
  open: boolean
  onClose: () => void
  onComplete: (data: Partial<CardActivity>) => void
  contextData?: Partial<CardActivity>
}

interface AudioData {
  url: string
  path: string
}

function RepeatAudioWizard({
  mode = 'add-pronunciation',
  open,
  onClose,
  onComplete,
  contextData,
}: RepeatAudioWizardProps) {
  const [currentStep, setCurrentStep] = useState<RepeatAudioStepKey>(stepsConfig[mode].initial)
  const [audioData, setAudioData] = useState<AudioData | null>(null)
  const [uploadingAudio, setUploadingAudio] = useState<boolean>(false)
  const [recordingState, setRecordingState] = useState('idle')
  const uid = useAuthUser().uid
  const { submitAudioResponse } = useOpenAI({
    audioPath: `user_data/${uid}`,
  })

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isInitialStep = currentStep === stepsConfig[mode].initial

  const { recorderState, startRecording, saveRecording: stopRecording } = useRecorder(30)
  const { audio, recording } = recorderState

  const handleRecord = () => {
    if (recording) {
      stopRecording()
      setRecordingState('stopped')
    } else {
      startRecording()
      setRecordingState('recording')
    }
  }

  async function processAudio(inputAudio: File | Blob) {
    await uploadAudio(inputAudio)
  }

  async function uploadAudio(inputAudio: File | Blob) {
    setUploadingAudio(true)
    try {
      const { url, fileName } = await submitAudioResponse(inputAudio)

      setAudioData({ url, path: fileName })

      return { url, path: fileName }
    } catch (err) {
      console.error('uploadAudio error: ', err)
    } finally {
      setUploadingAudio(false)
    }

    return { url: '', fileName: '' }
  }

  const handleNavigate = (nextStep: RepeatAudioStepKey) => {
    if (stepsConfig[mode].flow[currentStep].includes(nextStep)) {
      setCurrentStep(nextStep)
    }
  }

  useEffect(() => {
    if (!recording && recordingState === 'stopped' && audio) {
      processAudio(audio)
      handleNavigate('review-recorded-pronunciation')
    }
  }, [recordingState, recording, audio])

  useEffect(() => {
    if (open) {
      setCurrentStep(stepsConfig[mode].initial)
    }
  }, [open, mode])

  const CurrentStepComponent = steps[currentStep]

  return (
    <Dialog
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      open={open}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px',
          background: '#FFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
        },
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onClose={onClose}
    >
      {CurrentStepComponent ? (
        <CurrentStepComponent
          audioData={audioData}
          contextData={contextData}
          handleRecord={handleRecord}
          isInitialStep={isInitialStep}
          mode={mode}
          navigateTo={handleNavigate}
          recording={recording}
          recordingState={recordingState}
          setAudioData={setAudioData}
          uploadingAudio={uploadingAudio}
          onClose={onClose}
          onComplete={onComplete}
        />
      ) : null}
    </Dialog>
  )
}

export default RepeatAudioWizard
